/* eslint-disable react/prop-types */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Card,
  Checkbox,
  Col,
  Collapse,
  Form,
  Grid,
  message,
  Row,
  Table,
  Tooltip,
} from 'antd';
import {
  Button,
  COLORS,
  Text,
  Title,
} from 'components';
import { scrollToTop } from 'components/ScrollToTop';
import SearchForm from 'views/Payments/components/SearchForm';
import {
  getConvenios,
  getParcialidades,
  getPeriodosFiscales,
  postGenererReferenciasBanco,
  postGenererReferenciasNetpay,
  postReferenciasBanco,
} from 'api/recaudacion';
import gettersMap, { getAlcoholById } from 'api/padrones';
import { toCurrency } from 'utils/formatters';
import {
  RightOutlined,
  BarcodeOutlined,
  QuestionCircleOutlined,
  CreditCardOutlined,
} from '@ant-design/icons';
import NetPaybutton, {
  validateLastTransaction,
  getNetPayBtn,
  paymentLocationsEnum,
  clearDataset,
} from 'views/Payments/components/NetPayButton';
import Previewer from 'components/Previewer';
import debtsFormatter, { debtsColumns as columns } from 'utils/debtsFormatter';
import round from 'utils/round';
import { useSelector } from 'react-redux';
import { selectPublicoGeneral, selectRedondeo } from 'store/auth';
import {
  SHOW_REDONDEO,
  CLAVE_CATASTRAL_SEGMENTOS,
  ENV_NAME,
  ENV_NAMES_ENUM,
  PROD,
} from 'utils/env';
import { selectPadrones } from 'store/catalogos';
import { getAdeudoPadronAsync } from 'hooks/useAdeudoPadron';
import sortCargos from 'utils/sorterCargos';
import { useLocation, useNavigate } from 'react-router';
import { padronesEnum } from 'views/Tramites/GPM/enums';
import Spinner from 'components/Spinner';
import ModalAlertas from 'components/ModalAlertas';
import { DATETIME_FRONTEND_FORMAT } from 'utils/formatValues';
import { recievePadronFound } from './Form';
import ModalUpdateInfo from './components/ModalUpdateInfo';
import ModalBancos from './components/ModalBancos';

const RedondeoLabel = ({ rounded, tooltip }) => !!rounded && (tooltip ? (
  <Tooltip color={COLORS.white} title={<RedondeoLabel rounded={rounded} />}>
    {' '}
    <QuestionCircleOutlined />
  </Tooltip>
) : (
  <Row justify="end">
    <Text $xs>
      * Los centavos del monto total están siendo redondeados
      {SHOW_REDONDEO && rounded && ` (${toCurrency(rounded)})`}
    </Text>
  </Row>
));

const PaymentControls = ({
  _key,
  padron,
  cargos,
  loading,
  referencia,
  onClickOrder,
  onClickPayOnline,
  onClickTranferencia,
  total,
  totalProp = 'adeudo_total',
}) => {
  const getFolioReferencia = () => {
    if (referencia?.key === _key) {
      if (typeof referencia?.folio === 'string') {
        return referencia?.folio;
      }
      return referencia;
    }
    return '';
  };

  return (
    <>
      <br />
      <Row gutter={[10, 10]} justify="end">
        <NetPaybutton
          excludeValuesContactPublicoGeneral
          loading={loading}
          cargos={cargos}
          padron={padron}
          createdBy="DEBTS"
          totalProp={totalProp}
          generarReferencia={() => onClickPayOnline(cargos, _key)}
          referencia={getFolioReferencia()}
          total={!cargos?.length ? total : null}
        />
        <Col xs={24} sm={24} md={8}>
          <Button
          // disabled={referencia && referencia?.key !== _key}
            onClick={() => onClickOrder(cargos, _key)}
            loading={loading}
            block
            $accent
          >
            Generar orden de pagos
            <BarcodeOutlined />
          </Button>
        </Col>
        <Col xs={24} sm={24} md={8}>
          <Button
          // disabled={referencia && referencia?.key !== _key}
            onClick={() => onClickTranferencia(_key)}
            loading={loading}
            block
            $accent
          >
            Transferencia interbancaria
            <CreditCardOutlined />
          </Button>
        </Col>
      </Row>
    </>
  );
};

const PanelHeader = ({
  title,
  total,
  utilizaRedondeo,
  rounded,
  screens,
  active,
}) => (
  <>
    <Row justify="space-between">
      <Title level={4} $accent $uppercase>
        {title}
      </Title>
      <Title level={4} $accent style={{ textAlign: 'right', width: screens.md ? 'auto' : '100%' }}>
        {toCurrency(total)}
        {utilizaRedondeo && !screens.md && (<RedondeoLabel rounded={rounded} tooltip />)}
      </Title>
    </Row>
    <Row justify={utilizaRedondeo ? 'space-between' : 'start'}>
      <Text $xs $capitalize>
        <RightOutlined rotate={active ? 90 : 0} />
        {`${active ? 'D' : 'Ver d'}etalle del pago`}
      </Text>
      {utilizaRedondeo && screens.md && (<RedondeoLabel rounded={rounded} />)}
    </Row>
  </>
);

const TableConvenios = ({
  parcialidades,
  setSelectedRowKeys,
  selectedRowKeys,
}) => {
  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => {
      const _selectedKeys = [...selectedRowKeys];
      const _data = parcialidades.filter((item) => !item.saldado);
      const _filtered = _data.filter((item) => keys
        .includes(item.id)).map((p) => p.id);
      const _keys = _selectedKeys.length !== _filtered.length
        ? _filtered : [_data.map((p) => p.id)[0]];
      setSelectedRowKeys(_keys);
    },
    renderCell: (checked, record) => (
      <Checkbox
        checked={checked}
        disabled={record.saldado}
      />
    ),
  };

  const _columns = [
    {
      title: 'Mensualidad',
      dataIndex: 'mensualidad',
      render: (val) => (val === 0 ? 'Anticipo' : val),
      width: 100,
    },
    {
      title: 'Fecha de vencimiento',
      dataIndex: 'fecha_de_vencimiento',
      render: (val) => val?.format(DATETIME_FRONTEND_FORMAT),
      width: 100,
    },
    {
      title: 'Fecha de pago',
      dataIndex: 'fecha_de_pago',
      render: (val) => val?.format(DATETIME_FRONTEND_FORMAT),
      width: 100,
    },
    {
      title: 'Importe de la parcialidad',
      dataIndex: 'importe_de_la_parcialidad',
      render: (val, record) => (
        <b style={{ color: record?.saldado ? '' : COLORS.accent }}>
          {toCurrency(val)}
        </b>
      ),
      align: 'right',
      width: 100,
    },
  ];

  const scroll = { x: _columns.reduce((acum, curr) => acum + curr.width, 0) };

  const onRow = (record) => ({
    onClick: () => {
      if (record.saldado) return;
      const prev = parcialidades
        .filter((p) => p.mensualidad < record.mensualidad && !p.saldado)
        .map((p) => p.id);

      if (selectedRowKeys.includes(record.id)) {
        const clone = [...selectedRowKeys];
        clone.splice(selectedRowKeys.indexOf(record.id), 1);
        setSelectedRowKeys([...prev]);
      } else {
        setSelectedRowKeys([...prev, record.id]);
      }
    },
  });

  return (
    <Table
      columns={_columns}
      scroll={scroll}
      dataSource={parcialidades}
      rowKey="id"
      size="small"
      rowSelection={rowSelection}
      onRow={onRow}
      bordered
      pagination={{
        hideOnSinglePage: true,
        pageSize: parcialidades.length,
      }}
      rowClassName={(record) => {
        let className = '';
        if (record.saldado) {
          className = 'row-saldado';
        }
        return className;
      }}
    />
  );
};

const PanelCovenios = ({
  convenio,
  padron,
  referencia,
  screens,
  utilizaRedondeo,
  setReferencia,
  setActiveKey,
  setVisiblePreview,
  setVisibleBancos,
  setCargosTemp,
  setIsNewFolio,
  activeKey,
}) => {
  const padrones = useSelector(selectPadrones);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);

  const selected = convenio.parcialidades
    .filter((p) => selectedRowKeys.includes(p.id));
  const ttl = selected
    .reduce((acum, curr) => acum + curr.importe_de_la_parcialidad, 0);

  const [totalConvenio, roundedConvenio] = useMemo(() => round(ttl), [selectedRowKeys]);

  const generarReferencia = async (_key, callback, keys = []) => {
    const _selectedRowKeys = selectedRowKeys.length
      ? selectedRowKeys : keys;
    if (!_selectedRowKeys.length) {
      message.warn('Seleccione al menos una parcialidad para generar la referencia.');
      return;
    }

    if (referencia?.key !== _key || referencia?.parcialidadesLen !== _selectedRowKeys.length) {
      clearDataset(paymentLocationsEnum.DEBTS);
      setLoading(true);

      const cargos = padron.cargos_convenios.filter((c) => c.convenios === _key);
      let total = totalConvenio;
      if (totalConvenio === 0) {
        total = convenio.parcialidades.filter((p) => _selectedRowKeys.includes(p.id))
          .reduce((acum, curr) => acum + curr.importe_de_la_parcialidad, 0);
      }
      const _referencia = await postGenererReferenciasNetpay(
        padron.tipo_de_padron,
        padron,
        cargos,
        padrones,
        null,
        {},
        '',
        total,
        _key,
      );
      if (_referencia) {
        _referencia.key = _key;
        _referencia.parcialidadesLen = convenio
          .parcialidades
          .filter((p) => _selectedRowKeys.includes(p.id)).length;
        setReferencia(_referencia);
        setTimeout(callback, 1500);
      }
      setLoading(false);
    } else {
      setTimeout(callback, 1500);
    }
  };

  const setDefaultValues = () => {
    const parcialidadesSinSaldar = convenio.parcialidades.filter((p) => !p.saldado);
    if (parcialidadesSinSaldar.length) {
      setSelectedRowKeys([parcialidadesSinSaldar[0].id]);
    }
  };

  useEffect(() => {
    setDefaultValues();
  }, []);

  const onClickOrder = async (_, key) => {
    if (!selectedRowKeys.length) {
      message.warn('Seleccione al menos una parcialidad para generar la referencia.');
      return;
    }
    await generarReferencia(key, () => {
      localStorage.setItem(`${paymentLocationsEnum.DEBTS}_CONVENIO`, key);
      setVisiblePreview(true);
    });
  };

  const onClickPayOnline = async (_, key) => {
    if (!selectedRowKeys.length) {
      message.warn('Seleccione al menos una parcialidad para generar la referencia.');
      return;
    }
    setLoading(true);
    const cargos = padron.cargos_convenios
      .filter((c) => c.convenios === key);
    const _referencia = await postReferenciasBanco({
      tipoDePadron: padron.tipo_de_padron,
      padron,
      padrones,
      cargos,
      parcialidades: convenio.parcialidades.filter((p) => selectedRowKeys.includes(p.id)),
      totalProp: 'importe',
    });
    _referencia.key = key;
    setReferencia(_referencia);
    setTimeout(() => getNetPayBtn()?.click(), 1500);
    setLoading(false);
  };

  const onClickTranferencia = async (key) => {
    const _cargos = convenio?.parcialidades
      ?.filter((p) => selectedRowKeys.includes(p.id));

    setIsNewFolio(referencia?.antActiveKey !== key);
    setCargosTemp(_cargos);
    setVisibleBancos(true);
  };

  return (
    <Collapse
      key={`collapse_convenio_${convenio.id}`}
      onChange={setActiveKey}
      activeKey={activeKey}
      style={{ width: '100%', background: '#FFFFFF' }}
      bordered={false}
      destroyInactivePanel
      accordion
    >
      <Collapse.Panel
        showArrow={false}
        header={(
          <PanelHeader
            title={`CONVENIO ${convenio.folio}`}
            total={totalConvenio}
            utilizaRedondeo={utilizaRedondeo}
            rounded={roundedConvenio}
            screens={screens}
            active={activeKey === `convenio_${convenio.id}`}
          />
        )}
        id={`convenio_${convenio.id}`}
        key={`convenio_${convenio.id}`}
        bordered
      >
        <Card style={{ width: '100%' }} bodyStyle={{ padding: 0 }}>
          <TableConvenios
            parcialidades={convenio.parcialidades}
            selectedRowKeys={selectedRowKeys}
            setSelectedRowKeys={setSelectedRowKeys}
          />
          <>
            <br />
            <PaymentControls
              _key={convenio.key}
              padron={padron}
              cargos={selected}
              total={totalConvenio}
              loading={loading}
              referencia={referencia}
              onClickOrder={onClickOrder}
              onClickPayOnline={onClickPayOnline}
              onClickTranferencia={onClickTranferencia}
              totalProp="importe_de_la_parcialidad"
            />
          </>
        </Card>
      </Collapse.Panel>
    </Collapse>
  );
};

const CollapseCovenios = ({
  activeKey,
  convenios,
  padron,
  referencia,
  screens,
  setActiveKey,
  setReferencia,
  setVisiblePreview,
  utilizaRedondeo,
  setVisibleBancos,
  setCargosTemp,
  setIsNewFolio,
}) => {
  if (!convenios.length) return null;

  return convenios.map((convenio) => (
    <PanelCovenios
      convenio={convenio}
      padron={padron}
      referencia={referencia}
      screens={screens}
      utilizaRedondeo={utilizaRedondeo}
      setReferencia={setReferencia}
      setActiveKey={setActiveKey}
      setVisiblePreview={setVisiblePreview}
      setVisibleBancos={setVisibleBancos}
      setCargosTemp={setCargosTemp}
      setIsNewFolio={setIsNewFolio}
      activeKey={activeKey}
    />
  ));
};

const Debts = () => {
  // Hooks
  const location = useLocation();
  const ciudadanoPublicoGeneral = useSelector(selectPublicoGeneral);
  const utilizaRedondeo = useSelector(selectRedondeo);
  const padrones = useSelector(selectPadrones).filter((p) => p.available);
  const [loading, setLoading] = useState(false);
  const screens = Grid.useBreakpoint();
  const [form] = Form.useForm();
  const navigate = useNavigate();

  // State
  const [tipoDePadron, setTipoDePadron] = useState();
  const [padron, setPadron] = useState();
  const [periodosFiscales, setPeriodosFiscales] = useState([]);
  const [actualPeriod, setActualPeriod] = useState([]);
  const [visiblePreview, setVisiblePreview] = useState(false);
  const [visiblePreviewRecibo, setVisiblePreviewRecibo] = useState(false);
  const [referencia, setReferencia] = useState();
  const [recibo, setRecibo] = useState();
  const [visibleUpdate, setVisibleUpdate] = useState(false);
  const [idPadron, setIdPadron] = useState(0);
  const [tipoDePadronId, setTipoDePadronId] = useState(0);
  const [visibleBancos, setVisibleBancos] = useState(false);
  const [isNewFolio, setIsNewFolio] = useState(true);
  const [cargosTemp, setCargosTemp] = useState([]);
  const [GenerandoRecibo, setGenerandoRecibo] = useState(false);

  const [alertasInformativas, setAlertasInformativas] = useState([]);
  const [alertasRestrictivas, setAlertasRestrictivas] = useState([]);

  const [activeKey, setActiveKey] = useState([]);

  const [
    tramites = [],
    sistemas = [],
    cargosLibres = [],
    convenios = [],
    [total, rounded] = [],
  ] = useMemo(() => {
    if (!padron) return [];
    const idsTramites = padron.cargos.map((c) => c.tramite?.id).filter((s) => s);
    const _tramites = [...new Set(idsTramites)]
      .map((t) => padron.cargos.find((c) => c.tramite?.id === t).tramite)
      .map((trm) => ({
        ...trm,
        key: `tramite_${trm.id}`,
        cargos: sortCargos(padron.cargos.filter((c) => c.tramite?.id === trm.id)),
      }));

    const idsSistemas = [...new Set(padron.cargos
      .map((c) => (c.tramite || c.es_accesorio ? null : c.tipo_de_cargo.sistemas?.id))
      .filter((s) => s))];
    const _sistemas = idsSistemas.map((s) => {
      const cargosPadres = padron.cargos
        .filter((c) => !c.es_accesorio && c.tipo_de_cargo.sistemas?.id === s && !c.tramite);

      const accesorios = cargosPadres.map((cp) => padron.cargos
        .filter((_c) => _c.es_accesorio && _c.cargo_padre === cp.id)).flat();
      const cargos = sortCargos([...cargosPadres, ...accesorios]);
      const sistema = cargos[0].tipo_de_cargo.sistemas;
      return {
        key: `sistema_${sistema.id}`,
        ...sistema,
        cargos,
      };
    });
    const idsCargosSistemasTramites = _tramites.map((t) => t.cargos.map((c) => c.id)).flat()
      .concat(..._sistemas.map((s) => s.cargos.map((c) => c.id)).flat());
    const _cargosLibres = padron.cargos.filter((c) => !idsCargosSistemasTramites.includes(c.id));

    return [
      _tramites,
      _sistemas.filter((s) => s.cargos.length),
      sortCargos(_cargosLibres),
      padron.convenios,
      round(_cargosLibres.reduce((acum, curr) => acum + curr.adeudo_total, 0)),
    ];
  }, [padron]);

  const generarReferenciaBanco = async (_cargos) => {
    setLoading(true);
    const _referencia = await postGenererReferenciasBanco(
      tipoDePadron,
      padron,
      _cargos,
      padrones,
    );
    setReferencia({ ..._referencia, antActiveKey: activeKey });
    setIsNewFolio(false);
    setLoading(false);
    return _referencia;
  };

  const getTheLastYear = () => new Date().getFullYear();

  useEffect(() => {
    let mounted = true;
    const fetch = async () => {
      const _periodosFiscales = await getPeriodosFiscales(null, true);
      if (mounted) {
        setPeriodosFiscales(_periodosFiscales);
      }
      const lastPeriod = _periodosFiscales.find((e) => e.periodo === getTheLastYear()).id;
      setActualPeriod(lastPeriod);
      setLoading(false);
    };
    fetch();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    validateLastTransaction(false, (_recibo) => {
      if (_recibo) {
        setGenerandoRecibo(true);
        setRecibo(_recibo);
        setTimeout(() => {
          setGenerandoRecibo(false);
          setReferencia(_recibo);
          setVisiblePreviewRecibo(true);
        }, 3000);
      }
    }, setLoading, paymentLocationsEnum.DEBTS, true, setGenerandoRecibo);
  }, []);

  useEffect(() => {
    if (tipoDePadron && !padrones.some((e) => e.id === tipoDePadron)) {
      navigate('/not-found');
    }
  }, [tipoDePadron]);

  const onCancel = () => {
    setPadron();
    setCargosTemp();
    setVisiblePreview(false);
    setVisiblePreviewRecibo(false);
    setReferencia();
    setRecibo();
    form.resetFields(['q']);
    scrollToTop(screens);
  };

  const onCancelNetPay = () => {
    setVisiblePreviewRecibo(false);
    setRecibo();
  };

  const onFinishSearch = async (values) => {
    setLoading(true);
    const { padron: _padron, q, ...claveRfc } = values;
    const defaultParams = { q };
    const paramsObj = {
      3: {
        q: CLAVE_CATASTRAL_SEGMENTOS[ENV_NAME]
          ? [values?.localidad, values?.sector, values?.manzana, values?.lote, values?.condominio].join('-')
          : values.q,
      },
      4: {
        numero_de_identificacion_vehicular: values.serie,
        numero_de_placa_vigente: values.placa,
      },
      16: {
        serie: values.serie,
        numero_de_placa_vigente: values.placa,
      },
      17: {
        serie: values.serie,
        placa: values.placa,
      },
      6: ENV_NAME === 'nayarit' ? claveRfc : defaultParams,
      7: ENV_NAME === 'nayarit' ? claveRfc : defaultParams,
      8: ENV_NAME === 'nayarit' ? claveRfc : defaultParams,
      10: ENV_NAME === 'nayarit' ? claveRfc : defaultParams,
      11: ENV_NAME === 'nayarit' ? claveRfc : defaultParams,
      14: ENV_NAME === 'nayarit' ? claveRfc : defaultParams,
      26: ENV_NAME === 'nayarit' ? claveRfc : defaultParams,
      27: ENV_NAME === 'nayarit' ? claveRfc : defaultParams,
      28: ENV_NAME === 'nayarit' ? claveRfc : defaultParams,
    };
    const params = paramsObj[_padron] || defaultParams;
    const found = await gettersMap[_padron]({ ...params });
    if (found) {
      if (found?.alerta?.length) {
        const alertasVigentes = found.alerta.filter((a) => a.estados_globales !== 5);
        const _alertasRestrictivas = alertasVigentes.filter((a) => a.es_restrictiva);
        const _alertasInformativas = alertasVigentes.filter((a) => !a.es_restrictiva);
        setAlertasRestrictivas(_alertasRestrictivas);
        setAlertasInformativas(_alertasInformativas);
      }

      if (_padron === padronesEnum.ALCOHOL) {
        const { detail } = await getAlcoholById(found.id);
        if (detail) {
          message.warn(detail);
          setLoading(false);
          return;
        }
      }
      recievePadronFound({
        setLoading,
        setVisibleModalUpdateInfo: setVisibleUpdate,
        padron: found,
        inNeedsInfo: {
          setPadronId: setIdPadron,
          setTipoDePadron: setTipoDePadronId,
        },
        callback: async () => {
          setLoading(true);
          const _tipoDePadron = location?.state?.padron || _padron;
          const pg = ciudadanoPublicoGeneral
            && found.id === ciudadanoPublicoGeneral.id && tipoDePadron === 1;
          const [_cargos = [], err] = !pg
            ? await getAdeudoPadronAsync(tipoDePadron ?? _tipoDePadron, found.id, null, true)
            : [];
          if (found) {
            found.tipo_de_padron = _tipoDePadron;
          }
          const _cargosSinConvenios = _cargos.filter((c) => !c.convenios);
          const cargosConvenios = _cargos.filter((c) => c.convenios);
          if (cargosConvenios.length) {
            const conveniosIds = [...new Set(cargosConvenios.map((c) => c.convenios))];
            const _convenios = await Promise.all(conveniosIds.map((id) => getConvenios({ id })));
            const conveniosObj = _convenios.flat();
            const promises = conveniosObj.map((c) => getParcialidades(c.id));
            const _parcialidades = await Promise.all(promises);
            const parcialidades = _parcialidades.flat();
            const conveniosWithParcialidades = conveniosObj.map((c) => ({
              ...c,
              parcialidades: parcialidades.filter((p) => p.convenio === c.id),
            }));
            found.convenios = conveniosWithParcialidades;
            found.cargos_convenios = cargosConvenios;
          }
          if (err) {
            message.warn(err);
          } else if (_cargosSinConvenios.length) {
            if (location?.state?.cargo_id) {
              const cargoPadre = _cargosSinConvenios.find((c) => c.id === location.state.cargo_id);
              const cargosHijos = _cargosSinConvenios
                .filter((c) => c.cargo_padre === cargoPadre.id);
              found.cargos = debtsFormatter([cargoPadre, ...cargosHijos]);
            } else {
              found.cargos = debtsFormatter(_cargosSinConvenios);
            }
            setPadron(found);
          } else {
            message.info('Este padrón no tiene adeudos pendientes.');
          }
          setLoading(false);
        },
      });
    } else {
      setLoading(false);
    }
  };

  useEffect(() => {
    const autoFetchPadron = async () => {
      const { state } = location;
      if (state) {
        setTipoDePadron(state.padron);
        await onFinishSearch({ padron: 15, q: state.padron_RFC });
      }
    };
    autoFetchPadron();
  }, []);

  // region Generar referencia de orden de pago y pago en línea
  const generarReferencia = async (cargos, _key, callback, onlinePayment = false) => {
    if (referencia?.key !== _key) {
      clearDataset(paymentLocationsEnum.DEBTS);
      setLoading(true);
      const _referencia = await postGenererReferenciasNetpay(
        tipoDePadron,
        padron,
        cargos,
        padrones,
        null,
        {},
        'adeudo_total',
        0,
        null,
        onlinePayment,
      );
      if (_referencia) {
        _referencia.key = _key;
        setReferencia(_referencia);
        setTimeout(callback, 1500);
      }
      setLoading(false);
    } else {
      setTimeout(callback, 1500);
    }
  };

  const onClickOrder = async (
    cargos,
    key,
  ) => generarReferencia(cargos, key, () => setVisiblePreview(true));

  const onClickTranferencia = async (key) => {
    setCargosTemp([]);
    const finder = (s) => s.key === key;
    const _cargos = (sistemas.find(finder) || tramites.find(finder)
      || cargosLibres.find(finder))?.cargos;

    setIsNewFolio(referencia?.antActiveKey !== key);
    setCargosTemp(_cargos);
    setVisibleBancos(true);
  };

  const onClickPayOnline = async (
    cargos,
    key,
  ) => generarReferencia(cargos, key, () => getNetPayBtn()?.click(), true);

  if (!padron) {
    return (
      <>
        <Col span={24}>
          <Title style={{ textAlign: 'center', margin: 0 }}>
            Consulta de adeudos
          </Title>
        </Col>
        <SearchForm
          padron={padron}
          onCancel={onCancel}
          loading={loading}
          form={form}
          padrones={padrones}
          onFinishSearch={onFinishSearch}
          tipoDePadron={tipoDePadron}
          setTipoDePadron={setTipoDePadron}
          periodosFiscales={periodosFiscales}
          setLoading={setLoading}
        />
        <Previewer
          visible={visiblePreviewRecibo}
          base64={recibo?.base64}
          onCancel={onCancelNetPay}
          allowSend={Boolean(recibo?.base64)}
        />
        <ModalUpdateInfo
          visible={visibleUpdate}
          onCancel={() => setVisibleUpdate(false)}
          setVisible={setVisibleUpdate}
          tipoDePadron={tipoDePadron}
          idPadron={idPadron}
          tipoDePadronId={tipoDePadronId}
          callback={() => {
            form.submit();
          }}
        />
        <Spinner
          visible={GenerandoRecibo}
          loading={loading}
          generado={!!recibo}
          retryFunc={() => {
            window.location.reload();
          }}
        />
      </>

    );
  }

  const scroll = { x: columns.reduce((acum, curr) => acum + curr.width, 0) };

  const handleChangePanel = (key) => {
    setActiveKey([key]);
  };

  return (
    <>
      <Col span={24}>
        <Title style={{ textAlign: 'center', margin: 0 }}>
          Consulta de adeudos
        </Title>
      </Col>
      <SearchForm
        padron={padron}
        onCancel={onCancel}
        loading={loading}
        form={form}
        padrones={padrones}
        onFinishSearch={onFinishSearch}
        tipoDePadron={tipoDePadron}
        setTipoDePadron={setTipoDePadron}
        periodosFiscales={periodosFiscales}
        setLoading={setLoading}
      />
      {!!(tramites.length || sistemas.length || cargosLibres.length || convenios.length) && (
        <Row>
          <Title level={3} style={{ textAlign: 'center' }} $accent>
            Haz click sobre el adeudo para desplegar el desglose de los cargos
          </Title>
        </Row>
      )}
      {!!tramites.length && (
        <Card style={{ width: '100%' }}>
          <Title level={3} style={{ textAlign: 'center' }}>
            Adeudos en trámites
          </Title>
        </Card>
      )}
      {tramites.map((tramite) => {
        const _total = tramite.cargos.reduce((acum, curr) => acum + curr.adeudo_total, 0);
        const [totalTramite, roundedTramite] = round(_total);
        return (
          <Collapse
            key={`collapse_tramite_${tramite.id}`}
            activeKey={tramites.map((t) => t.key).includes(activeKey) ? activeKey : undefined}
            onChange={handleChangePanel}
            style={{ width: '100%', background: '#FFFFFF' }}
            bordered={false}
            destroyInactivePanel
            accordion
          >
            <Collapse.Panel
              showArrow={false}
              header={(
                <PanelHeader
                  title={`TRÁMITE ${tramite.folio}`}
                  total={totalTramite}
                  utilizaRedondeo={utilizaRedondeo}
                  rounded={roundedTramite}
                  screens={screens}
                  active={activeKey === `tramite_${tramite.id}`}
                />
              )}
              key={`tramite_${tramite.id}`}
            >
              <Card style={{ width: '100%' }} bodyStyle={{ padding: 0 }}>
                <Table
                  columns={columns}
                  scroll={scroll}
                  dataSource={tramite.cargos}
                  rowKey="id"
                  loading={loading}
                  size="small"
                  bordered
                />
                <PaymentControls
                  _key={tramite.key}
                  padron={padron}
                  cargos={tramite.cargos}
                  loading={loading}
                  referencia={referencia}
                  onClickOrder={onClickOrder}
                  onClickPayOnline={onClickPayOnline}
                  onClickTranferencia={onClickTranferencia}
                />
              </Card>
            </Collapse.Panel>
          </Collapse>
        );
      })}
      {!!sistemas.length && (
        <Card style={{ width: '100%' }}>
          <Title level={3} style={{ textAlign: 'center' }}>
            Adeudos en sistema
          </Title>
        </Card>
      )}
      {sistemas.map((sistema) => {
        const _total = sistema.cargos.reduce((acum, curr) => acum + curr.adeudo_total, 0);
        const [totalSistema, roundedSistema] = round(_total);
        return (
          <Collapse
            key={`collapse_sistema_${sistema.id}`}
            onChange={handleChangePanel}
            style={{ width: '100%', background: '#FFFFFF' }}
            bordered={false}
            activeKey={activeKey}
            destroyInactivePanel
            accordion
          >
            <Collapse.Panel
              showArrow={false}
              header={(
                <PanelHeader
                  title={`SISTEMA ${sistema.descripcion}`}
                  total={totalSistema}
                  utilizaRedondeo={utilizaRedondeo}
                  rounded={roundedSistema}
                  screens={screens}
                  active={activeKey === `sistema_${sistema.id}`}
                />
              )}
              key={`sistema_${sistema.id}`}
              bordered
            >
              <Card style={{ width: '100%' }} bodyStyle={{ padding: 0 }}>
                <Table
                  columns={columns}
                  scroll={scroll}
                  dataSource={sistema.cargos}
                  rowKey="id"
                  loading={loading}
                  size="small"
                  bordered
                />
                <br />
                <PaymentControls
                  _key={sistema.key}
                  padron={padron}
                  cargos={sistema.cargos}
                  loading={loading}
                  referencia={referencia}
                  onClickOrder={onClickOrder}
                  onClickPayOnline={onClickPayOnline}
                  onClickTranferencia={onClickTranferencia}
                />
              </Card>
            </Collapse.Panel>
          </Collapse>
        );
      })}
      {!!convenios.length && !PROD && (
        <>
          <Card style={{ width: '100%' }}>
            <Title level={3} style={{ textAlign: 'center' }}>
              Convenios
            </Title>
          </Card>
          <Card style={{ width: '100%', marginTop: 20 }} bodyStyle={{ padding: 0 }}>
            <CollapseCovenios
              convenios={convenios}
              setActiveKey={handleChangePanel}
              utilizaRedondeo={utilizaRedondeo}
              screens={screens}
              activeKey={activeKey}
              padron={padron}
              setLoading={setLoading}
              setVisiblePreview={setVisiblePreview}
              setReferencia={setReferencia}
              referencia={referencia}
              setVisibleBancos={setVisibleBancos}
              setCargosTemp={setCargosTemp}
              setIsNewFolio={setIsNewFolio}
            />
          </Card>
        </>
      )}

      <Card style={{ width: '100%', marginTop: 20 }} bodyStyle={{ padding: 0 }}>
        <Collapse
          activeKey={activeKey === 'adeudos' ? activeKey : undefined}
          onChange={handleChangePanel}
          style={{ width: '100%', background: '#FFFFFF' }}
          destroyInactivePanel
          accordion
          ghost
        >
          <Collapse.Panel
            showArrow={false}
            header={(
              <PanelHeader
                title="Otros adeudos"
                total={total}
                utilizaRedondeo={utilizaRedondeo}
                rounded={rounded}
                screens={screens}
                active={activeKey === 'adeudos'}
              />
            )}
            key="adeudos"
            collapsible={!cargosLibres.length && 'disabled'}
            bordered
          >
            <Table
              columns={columns}
              scroll={scroll}
              dataSource={cargosLibres}
              rowKey="id"
              loading={loading}
              size="small"
              bordered
            />
            <PaymentControls
              _key="adeudos"
              padron={padron}
              cargos={cargosLibres}
              loading={loading}
              referencia={referencia}
              onClickOrder={onClickOrder}
              onClickPayOnline={onClickPayOnline}
              onClickTranferencia={onClickTranferencia}
            />
          </Collapse.Panel>
        </Collapse>
      </Card>
      <Previewer
        visible={visiblePreview}
        base64={referencia?.base64}
        onCancel={() => setVisiblePreview(false)}
      />
      {ENV_NAME === ENV_NAMES_ENUM.NAYARIT && (
        <ModalBancos
          visible={visibleBancos}
          onCancel={() => { setVisibleBancos(false); }}
          cargosGenerados={cargosTemp}
          padron={padron}
          padrones={padrones}
          tipoDePadron={tipoDePadron}
          currentClassifier="Consulta de adeudos"
          cbReferenciaBanco={() => generarReferenciaBanco(cargosTemp)}
          isNewFolio={isNewFolio}
          periodoFiscal={actualPeriod}
        />
      )}
      <ModalAlertas
        restrictivas={alertasRestrictivas}
        informativas={alertasInformativas}
        callback={onCancel}
      />
    </>
  );
};

export default Debts;
